import { FunctionComponent, useState } from 'react';
import Link from 'next/link';
import { Form, Formik } from 'formik';
import { boolean, object, string } from 'yup';
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import {
    Button,
    Modal,
    Text,
    Textarea,
    UnderlineTag,
    ValuationStamps,
} from '@/atoms';
import { EvaluationForm } from '@/molecules';

import { TypeRatingProps } from './types';
import { ButtonVariant } from '@/atoms/button/types';
import { oneLine } from 'common-tags';
import { gtmPush } from '@/utils';

/**
 * Rating
 */
export const Rating: FunctionComponent<TypeRatingProps> = ({
    className = '',
    titleLeft,
    descriptionLeft,
    textAreaText,
    titleRight,
    textButtonStepper,
    onClickLinkMobile,
    setModal,
    bridle,
    setPublicado,
}: TypeRatingProps) => {
    const INPUT_TOO_SHORT_MESSAGE = 'Demasiado corto!';
    const INPUT_TOO_LONG_MESSAGE = 'Demasiado largo';

    const RatingSchema = object().shape({
        nameAndLastName: string()
            .min(2, INPUT_TOO_SHORT_MESSAGE)
            .max(50, INPUT_TOO_LONG_MESSAGE)
            .required('El nombre y apellidos es Obligatorio'),
        emailEvaluation: string()
            .min(2, INPUT_TOO_SHORT_MESSAGE)
            .max(50, INPUT_TOO_LONG_MESSAGE)
            .required('El email es Obligatorio'),
        evaluationPolicy: boolean()
            .oneOf([true], 'Debes aceptar las políticas de privacidad'),
        informationPolicy: boolean(),
        opinion: string()
            .min(2, INPUT_TOO_SHORT_MESSAGE)
            .max(256, INPUT_TOO_LONG_MESSAGE),
    });
    const initialValues = {
        nameAndLastName: '',
        emailEvaluation: '',
        opinion: '',
        evaluationPolicy: false,
        informationPolicy: false,
        color: 1,
        smell: 1,
        texture: 1,
        taste: 1,
        general_value: 1,
        bridle: bridle,
    };

    const { executeRecaptcha } = useGoogleReCaptcha();

    const [currentStep, setCurrentStep] = useState(1);
    const [isMensaje, setIsMensaje] = useState(false);

    return (
        <>
            <Formik
                onSubmit={async ({
                    bridle,
                    emailEvaluation,
                    nameAndLastName,
                    evaluationPolicy,
                    informationPolicy,
                    color,
                    smell,
                    texture,
                    taste,
                    general_value,
                }, { resetForm }) => {
                    const json = {
                        webform_id: 'rating',
                        gReCaptchaToken: await executeRecaptcha("enquiryFormSubmit"),
                        bridle: bridle,
                        email: emailEvaluation,
                        name: nameAndLastName,
                        accept_privacy: evaluationPolicy,
                        accept_commercial: informationPolicy,
                        color: color,
                        smell: smell,
                        texture: texture,
                        taste: taste,
                        general_value: general_value,
                    }

                    await fetch('/api/submitform', {
                        method: "POST",
                        body: JSON.stringify(json),
                    })
                    gtmPush({
                        event: 'generate_lead',
                        lead_type: 'valoracion de producto',
                        color: color,
                        olfato: smell,
                        textura: texture,
                        sabor: taste,
                        valoracion_general: general_value,
                    });

                    setIsMensaje(true);
                    setPublicado(true);
                    resetForm({ values: initialValues })

                }}
                initialValues={initialValues}
                validationSchema={RatingSchema}
            >
                {({
                    isSubmitting, values
                }) => (
                    <Form noValidate>
                        <div className={`flex ${className}`}>
                            <div
                                className={oneLine`
              px-6 pt-[72px] md:px-20 lg:pb-[118px] w-[810px] sh-full bg-primary-50 
              ${currentStep === 1 ? 'flex flex-col' : 'hidden lg:flex'}
              `}
                            >
                                <UnderlineTag
                                    theme="light"
                                    position="left"
                                    className="lg:hidden mb-4"
                                >
                                    Paso 1/2
                                </UnderlineTag>
                                <Text className=" text-headline text-headline--h2 mb-4" as="h2">
                                    {titleLeft}
                                </Text>
                                <Text className="text-body text-body--l" as="p">
                                    {descriptionLeft}
                                </Text>
                                <hr className="w-full text-primary-200 mt-10 mb-6" />
                                <div className="flex flex-wrap gap-x-[65px] gap-y-6">
                                    <ValuationStamps text="Color" name="color" />
                                    <ValuationStamps text="Olfato" name="smell" />
                                    <ValuationStamps text="Textura" name="texture" />
                                    <ValuationStamps text="Sabor" name="taste" />
                                    <ValuationStamps text="Valoración general" name="general_value" />
                                </div>
                                <hr className="w-full text-primary-200 mb-10 mt-8" />
                                <Textarea
                                    label={textAreaText}
                                    name="opinion"
                                    placeholder="¡Riquísimo!"
                                />
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    onClick={() => setCurrentStep(2)}
                                    className="my-6 lg:hidden"
                                    type={currentStep == 2 ? 'submit' : 'button'}
                                >
                                    {textButtonStepper}
                                </Button>
                            </div>
                            <div
                                className={oneLine`
              px-6 sm:px-20 py-[72px] w-[544px] h-full
              ${currentStep === 2 ? 'flex flex-col' : 'hidden lg:flex'}
          `}
                            >
                                <UnderlineTag
                                    theme="light"
                                    position="left"
                                    className="lg:hidden mb-6"
                                >
                                    Paso 2/2
                                </UnderlineTag>
                                <EvaluationForm
                                    backgroundColor="#FFFFFF"
                                    emailLabel="Email"
                                    labelCkeckDown="Sí, acepto el envío de información publicitaria o promocional relativa a CFG (marca Navidul) de acuerdo con lo informado"
                                    nameLabel="Nombre y Apellidos"
                                    textButton="enviar valoración"
                                    textLinkMobile="volver atrás"
                                    textLink=""
                                    title={titleRight}
                                    onClickLinkMobile={() => setCurrentStep(1)}
                                    onClickLink={onClickLinkMobile}
                                    isSubmitting={isSubmitting}
                                    valueInformationPolicy={values.informationPolicy}
                                    valueEvaluationPolicy={values.evaluationPolicy}
                                >
                                    <p>
                                        He leído y acepto la{' '}
                                        <Link
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="!leading-5 text-primary-500 hover:text-primary-600 my-4"
                                            href="/es/politica-privacidad">
                                            política de privacidad.
                                        </Link>
                                    </p>
                                </EvaluationForm>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik >

            <Modal
                isOpen={isMensaje}
                closeModal={() => setIsMensaje(false)}
                className="sm:w-[590px]"
            >
                <div className="flex flex-col gap-10">
                    <div className="flex flex-col gap-4 text-center">
                        <Text className="text-subtitle text-subtitle--l">
                            ¡Gracias por tu valoración!
                        </Text>
                        <Text className="text-body text-body--l text-text-secondary">
                            Tu valoración se ha enviado correctamente. Gracias por ayudarnos a
                            mejorar cada día. Consigue 10% de descuento en tu próxima compra
                            usando el código <b>TRAZABILIDAD</b> durante el proceso de compra.
                        </Text>
                    </div>
                    <div className="flex flex-col justify-center gap-5 md:flex-row">
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            onClick={() => {
                                setIsMensaje(false);
                                setModal(false);
                            }}
                        >
                            Cerrar
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

Rating.displayName = 'Rating';
